import type { RowData } from "@tanstack/react-table";
import type { DataGridCellAddress } from "../cellGridTypes";
import type { DataGridRow } from "../columns/types";
import { extendRange } from "../features/range/rangeActions";
import { HEADER_ROW_HEIGHT, ROW_HEIGHT_BY_DENSITY } from "../layout/density";
import type { DataGridInstance } from "../types";
import type { GridController } from "./gridController";
import type { GridDirection, GridKeyAction } from "./keyMap";
import {
    findFirstFocusableRowIndex,
    findFocusableRowIndex,
    focusHeader,
    getRowModeColumnId,
} from "./navigation";
import { endSelectionRun, stepSelectionRun } from "./selectionRun";

export interface GridActionInput<TData extends RowData> {
    action: GridKeyAction;
    instance: DataGridInstance<TData>;
    controller: GridController;
    /** The element that received the key press. */
    target: EventTarget;
}

/** The focused cell and its row's index in the rendered rows (-1 when off-screen). */
export interface FocusedRow<TData extends RowData> {
    rows: ReadonlyArray<DataGridRow<TData>>;
    current: DataGridCellAddress | null;
    rowIndex: number;
}

export interface Cursor<TData extends RowData> extends FocusedRow<TData> {
    input: GridActionInput<TData>;
    columnIds: readonly string[];
    columnIndex: number;
}

export const focusAt = <TData extends RowData>(
    cursor: Cursor<TData>,
    { rowIndex, columnId }: { rowIndex: number; columnId?: string },
) => {
    const { instance, controller } = cursor.input;
    const index = findFocusableRowIndex({
        rows: cursor.rows,
        isSeparator: instance.rowKinds.isSeparator,
        rowIndex,
        fromIndex: cursor.rowIndex,
    });
    const row = cursor.rows[index];
    const nextColumnId =
        columnId ??
        cursor.current?.columnId ??
        getRowModeColumnId(instance.table);
    if (!row || nextColumnId === null) {
        return false;
    }
    instance.table.setFocusedCell(row.id, nextColumnId);
    controller.focusTarget({
        rowId: row.id,
        columnId: instance.hasCellNavigation ? nextColumnId : null,
    });
    return true;
};

export const pageSize = <TData extends RowData>({ input }: Cursor<TData>) => {
    const viewportHeight = input.controller.viewport?.clientHeight ?? 0;
    const rowHeight = ROW_HEIGHT_BY_DENSITY[input.instance.density];
    return Math.max(
        1,
        Math.floor((viewportHeight - HEADER_ROW_HEIGHT) / rowHeight) - 1,
    );
};

const moveHorizontal = <TData extends RowData>(
    cursor: Cursor<TData>,
    direction: "left" | "right",
) => {
    const row = cursor.rows[cursor.rowIndex];
    const { hasCellNavigation } = cursor.input.instance;
    const isOnFirstCell = cursor.columnIndex <= 0;
    if (hasCellNavigation && !isOnFirstCell && direction === "left") {
        return focusAt(cursor, {
            rowIndex: cursor.rowIndex,
            columnId: cursor.columnIds[cursor.columnIndex - 1],
        });
    }
    const canTreeMove = !hasCellNavigation || isOnFirstCell;
    if (direction === "left" && canTreeMove) {
        if (row.getCanExpand() && row.getIsExpanded()) {
            row.toggleExpanded(false);
            return true;
        }
        const parent = row.getParentRow();
        const parentIndex = parent
            ? cursor.input.controller.rowIndexById.get(parent.id)
            : undefined;
        return parentIndex === undefined
            ? hasCellNavigation
            : focusAt(cursor, { rowIndex: parentIndex });
    }
    if (canTreeMove && row.getCanExpand() && !row.getIsExpanded()) {
        cursor.input.controller.skipExpandFade();
        row.toggleExpanded(true);
        return true;
    }
    if (hasCellNavigation) {
        const nextColumnId = cursor.columnIds[cursor.columnIndex + 1];
        return nextColumnId
            ? focusAt(cursor, {
                  rowIndex: cursor.rowIndex,
                  columnId: nextColumnId,
              })
            : true;
    }
    return row.getIsExpanded() && row.subRows.length > 0
        ? focusAt(cursor, { rowIndex: cursor.rowIndex + 1 })
        : false;
};

export const move = <TData extends RowData>(
    cursor: Cursor<TData>,
    direction: GridDirection,
) => {
    if (direction === "up") {
        const firstRowIndex = findFirstFocusableRowIndex(
            cursor.rows,
            cursor.input.instance.rowKinds.isSeparator,
        );
        return cursor.rowIndex <= firstRowIndex
            ? focusHeader(
                  cursor.input.controller.root,
                  cursor.current?.columnId ?? null,
              )
            : focusAt(cursor, { rowIndex: cursor.rowIndex - 1 });
    }
    if (direction === "down") {
        return focusAt(cursor, { rowIndex: cursor.rowIndex + 1 });
    }
    return moveHorizontal(cursor, direction);
};

export const canSelectByKeyboard = <TData extends RowData>(
    cursor: Cursor<TData>,
    row: DataGridRow<TData>,
) =>
    row.getCanSelect() &&
    !cursor.input.instance.optionsRef.current.rowSelection
        ?.isKeyboardSelectionDisabled;

/** The nearest row past the cursor in `step`'s direction that can be selected; -1 when none. */
const findSelectableIndex = <TData extends RowData>(
    cursor: Cursor<TData>,
    step: number,
) => {
    for (
        let index = cursor.rowIndex + step;
        index >= 0 && index < cursor.rows.length;
        index += step
    ) {
        if (cursor.rows[index]?.getCanSelect()) {
            return index;
        }
    }
    return -1;
};

/**
 * Shift+arrow: grows the cell range in cell navigation, or the row selection
 * run in row mode, stepping over separator rows and rows that can't be
 * selected, as Shift+click does. An unselectable parent is stepped over, never
 * selected through to its lines. From a row that can't be selected (a group
 * header reached with a plain arrow), it selects and focuses the next
 * selectable row, where the next step anchors.
 */
export const extend = <TData extends RowData>(
    cursor: Cursor<TData>,
    direction: GridDirection,
) => {
    const { instance } = cursor.input;
    if (instance.hasCellNavigation) {
        return (
            instance.hasRangeSelection && extendRange(instance.table, direction)
        );
    }
    const row = cursor.rows[cursor.rowIndex];
    if (
        direction === "left" ||
        direction === "right" ||
        instance.optionsRef.current.rowSelection?.isKeyboardSelectionDisabled
    ) {
        return false;
    }
    const nextIndex = findSelectableIndex(cursor, direction === "up" ? -1 : 1);
    const next = cursor.rows[nextIndex];
    if (!row.getCanSelect()) {
        if (!next) {
            return false;
        }
        endSelectionRun(cursor.input.controller);
        next.toggleSelected(true);
        return focusAt(cursor, { rowIndex: nextIndex });
    }
    if (!next) {
        row.toggleSelected(true);
        return true;
    }
    stepSelectionRun({ controller: cursor.input.controller, row, next });
    return focusAt(cursor, { rowIndex: nextIndex });
};
